/**
 * A pose is one keyframe of a layer animation, in units that survive resizing: translation in
 * percent of the element's own box, angles in degrees, blur in px at a 1080 px canvas (scaled to
 * the real canvas). `poseFrames` turns a list of poses into Web Animations keyframes that every
 * consumer (editor preview, HTML5 export, video renderer) plays the same way.
 */

export interface ClipInset {
  type: 'inset';
  /** Percent of the element's box hidden from each side; negative values leave room for shadows. */
  top: number;
  right: number;
  bottom: number;
  left: number;
}
export interface ClipCircle {
  type: 'circle';
  /** Radius in percent (of the box diagonal / √2), centred at x/y percent of the box. */
  r: number;
  x?: number;
  y?: number;
}
export type ClipShape = ClipInset | ClipCircle;

export interface Pose {
  /** Translation, percent of the element's own width/height. */
  x?: number;
  y?: number;
  scale?: number;
  scaleX?: number;
  scaleY?: number;
  rotate?: number;
  rotateX?: number;
  rotateY?: number;
  skewX?: number;
  skewY?: number;
  /** Multiplies the layer's own opacity. */
  opacity?: number;
  /** px at a 1080 px canvas. */
  blur?: number;
  brightness?: number;
  clip?: ClipShape;
  /** 0..1 position in the animation; spread evenly when omitted. */
  offset?: number;
  /** Easing from this keyframe to the next one (CSS timing function). */
  easing?: string;
}

export type CssKeyframe = Record<string, string | number | null | undefined>;

export const SHOWN_INSET: ClipInset = {
  type: 'inset',
  top: -25,
  right: -25,
  bottom: -25,
  left: -25,
};

const round = (v: number, p = 1000) => Math.round(v * p) / p;

function clipCss(c: ClipShape): string {
  if (c.type === 'circle')
    return `circle(${round(c.r)}% at ${round(c.x ?? 50)}% ${round(c.y ?? 50)}%)`;
  return `inset(${round(c.top)}% ${round(c.right)}% ${round(c.bottom)}% ${round(c.left)}%)`;
}

export interface PoseFrameOptions {
  /** The layer's own rotation, kept under the animation (replace mode only). */
  baseRotation?: number;
  /** The layer's own opacity, multiplied by the pose opacity. */
  baseOpacity?: number;
  /** Converts the 1080-based blur to the real canvas. */
  blurScale?: number;
  /**
   * `add`: the frames are composited on top of other animations (emphasis), so they carry only
   * the delta and never the layer's own rotation or opacity.
   */
  additive?: boolean;
}

/**
 * Convert poses to keyframes. All frames share the same transform and filter function lists so
 * the browser interpolates them function by function (no matrix decomposition surprises).
 */
export function poseFrames(poses: Pose[], opts: PoseFrameOptions = {}): CssKeyframe[] {
  const base = opts.additive ? 0 : (opts.baseRotation ?? 0);
  const baseOpacity = opts.additive ? 1 : (opts.baseOpacity ?? 1);
  const blurScale = opts.blurScale ?? 1;
  const has = (k: keyof Pose) => poses.some((p) => p[k] !== undefined);
  const hasTranslate = has('x') || has('y');
  const hasRotate = has('rotate') || base !== 0;
  const has3d = has('rotateX') || has('rotateY');
  const hasSkew = has('skewX') || has('skewY');
  const hasScale = has('scale') || has('scaleX') || has('scaleY');
  const hasTransform = hasTranslate || has('rotate') || has3d || hasSkew || hasScale;
  const hasBlur = has('blur');
  const hasBrightness = has('brightness');
  const hasOpacity = has('opacity');
  const hasClip = has('clip');
  const clipType = poses.find((p) => p.clip)?.clip?.type;

  return poses.map((p) => {
    const frame: CssKeyframe = {};
    if (hasTransform) {
      const parts: string[] = [];
      if (has3d) parts.push('perspective(800px)');
      if (hasTranslate) parts.push(`translate(${round(p.x ?? 0)}%, ${round(p.y ?? 0)}%)`);
      if (hasRotate) parts.push(`rotate(${round(base + (p.rotate ?? 0))}deg)`);
      if (has3d)
        parts.push(`rotateX(${round(p.rotateX ?? 0)}deg) rotateY(${round(p.rotateY ?? 0)}deg)`);
      if (hasSkew) parts.push(`skew(${round(p.skewX ?? 0)}deg, ${round(p.skewY ?? 0)}deg)`);
      if (hasScale) {
        const s = p.scale ?? 1;
        parts.push(
          `scale(${round(s * (p.scaleX ?? 1), 10000)}, ${round(s * (p.scaleY ?? 1), 10000)})`,
        );
      }
      frame.transform = parts.join(' ');
    }
    if (hasOpacity)
      frame.opacity = round(Math.max(0, Math.min(1, (p.opacity ?? 1) * baseOpacity)), 10000);
    if (hasBlur || hasBrightness) {
      const parts: string[] = [];
      if (hasBlur) parts.push(`blur(${round(Math.max(0, (p.blur ?? 0) * blurScale), 100)}px)`);
      if (hasBrightness) parts.push(`brightness(${round(p.brightness ?? 1)})`);
      frame.filter = parts.join(' ');
    }
    if (hasClip) {
      const c =
        p.clip ?? (clipType === 'circle' ? { type: 'circle' as const, r: 80 } : SHOWN_INSET);
      frame.clipPath = clipCss(c);
    }
    if (p.offset !== undefined) frame.offset = p.offset;
    if (p.easing) frame.easing = p.easing;
    return frame;
  });
}

/** Mirror a list of poses in time (enter → exit). */
export function reversePoses(poses: Pose[]): Pose[] {
  const n = poses.length;
  return poses
    .map((p, i) => ({
      ...p,
      offset: p.offset === undefined ? undefined : 1 - p.offset,
      // The easing of a segment belongs to its start keyframe; reversed, it moves one step back
      easing: i > 0 ? poses[i - 1].easing : undefined,
    }))
    .reverse()
    .map((p, i) => (i === n - 1 ? { ...p, easing: undefined } : p));
}
